import * as React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Slider from "@mui/material/Slider";
import moment from "moment";
import BarChart from "../BarChart";
import RegressionInfo from "./RegressionInfo";
import PieChart from "../PieChart";
import { useTranslation } from "react-i18next";

function valuetext(value) {
  return `${moment.utc(value).format("DD/MM HH:mm")}`;
}
function optiontext(value) {
  return `${moment.utc(value).format("DD/MM")}`;
}
export default function CompareTimeRanges({
  data,
  quantitativeLabelsChecked,
  choiceLabelsChecked,
  table,
  selectedLabels,
  labelOpacity,
  setLabelOpacity,
  thumbnail,
  error,
  showValues,
  verticalBars,
  pieChart,
  sortDec,
}) {
  const { t } = useTranslation();
  const times = data.map((row) => row.time);
  const minValue = 0;
  const minTime = times[minValue];
  const maxValue = times.length - 1;
  const maxTime = times[maxValue];
  const defaultValue = Math.round((minValue + maxValue) / 2);
  const [value, setValue] = React.useState(defaultValue);

  const valueLabelFormat = (value) => {
    return valuetext(times[value]);
  };
  const options = [
    "No option",
    t("moodboards.placeholders.fromTo", {
      from: optiontext(minTime),
      to: optiontext(times[value]),
    }),
    t("moodboards.placeholders.fromTo", {
      from: optiontext(times[value]),
      to: optiontext(maxTime),
    }),
  ];
  //const options = ["No option", `Range A`, `Range B`];
  const categoricalRegression = window.categoricalRegression;

  const analyses = quantitativeLabelsChecked.map(
    ({ id: quantitativeId, label: quantitativeLabel, question }) => {
      let labelCategories = [];
      data.forEach((beep) => {
        let quantitativeValue = null;
        let qualitativeValue = null;
        beep.table.forEach((row) => {
          if (row.id === quantitativeId) quantitativeValue = row.score;
          if (beep.time < times[value]) qualitativeValue = [1];
          if (beep.time >= times[value]) qualitativeValue = [2];
        });

        if (quantitativeValue !== null && qualitativeValue !== null) {
          let bits = new Array(options.length).fill(0);
          qualitativeValue.forEach((p) => {
            bits[p - 1] = 1;
          });
          labelCategories.push({
            quantitativeId,
            quantitativeLabel,
            quantitativeValue,
            qualitativeValue,
            bits,
          });
        }
      });
      //console.log(labelCategories);

      const valuesForCatReg = labelCategories.flatMap(
        ({ quantitativeValue }) => quantitativeValue
      );
      //console.log(valuesForCatReg);
      const indicesForCatReg = labelCategories.flatMap(({ bits }) => bits);
      //console.log(indicesForCatReg);
      return {
        ...categoricalRegression(valuesForCatReg, indicesForCatReg),
        id: quantitativeId,
        label: quantitativeLabel,
        question,
      };
    }
  );
  //console.log(analyses);

  let barchart = options.flatMap((option, index) => {
    let dataObj = {};

    analyses.forEach((analyse) => {
      // don't display "No chosen" if number of measurments (NM) is 0
      dataObj["name"] = option;

      let value = analyse.M[index];
      if (value > 100) value = 100;
      if (value < 0) value = 0;
      value = Math.round((value + Number.EPSILON) * 100) / 100;

      const errorMin = !isNaN(analyse.DELTAPLOW[index])
        ? analyse.DELTAPLOW[index] * 100
        : 0;
      const errorMax = !isNaN(analyse.DELTAPUP[index])
        ? analyse.DELTAPUP[index] * 100
        : 0;
      const error = [errorMin, errorMax];

      if (!isNaN(value)) {
        dataObj[analyse.id] = value;
        dataObj[`${analyse.id}$label`] = analyse.label;
        dataObj[analyse.id + "$error"] = error;
        dataObj[analyse.id + "$numberOfMeasurements"] = analyse.NM[index];
      }
    });
    return dataObj;
  });
  if (analyses?.[0]?.NM?.[0] === 0 || isNaN(analyses?.[0]?.NM?.[0]))
    barchart.shift();
  //console.log(barchart);

  return (
    <div>
      {pieChart ? (
        <PieChart
          data={barchart}
          quantitativeLabelsChecked={quantitativeLabelsChecked}
          opacity={labelOpacity}
          height={!thumbnail ? 250 : 80}
          thumbnail={thumbnail}
          showValues={showValues}
          sortDec={sortDec}
        />
      ) : (
        <BarChart
          data={barchart}
          quantitativeLabelsChecked={quantitativeLabelsChecked}
          opacity={labelOpacity}
          height={!thumbnail ? 250 : 80}
          thumbnail={thumbnail}
          error={error}
          showValues={showValues}
          verticalBars={verticalBars}
          sortDec={sortDec}
          lineBreak={true}
        />
      )}
      {!thumbnail ? (
        <RegressionInfo
          category={t("moodboards.timeRanges")}
          analyses={analyses}
        />
      ) : null}
      <Box paddingTop={4} display={"flex"} gap={1} alignItems={"center"}>
        <Typography textAlign={"center"} variant="body2">
          {valuetext(minTime)}
        </Typography>
        <Slider
          value={value}
          onChange={(_, newValue) => setValue(newValue)}
          valueLabelDisplay="on"
          valueLabelFormat={valueLabelFormat}
          min={minValue}
          max={maxValue}
          track={false}
          aria-labelledby="track-false-slider"
          getAriaValueText={valuetext}
        />
        <Typography textAlign={"center"} variant="body2">
          {valuetext(maxTime)}
        </Typography>
      </Box>
    </div>
  );
}
